// useOAuthPopup.ts
import { TOAuthProvider } from '@talon/web/lib/api';
import { env } from '@talon/web/src/config/env';
import { useCallback } from 'react';

type OAuthPopupResult =
  | { status: 'success'; provider: TOAuthProvider }
  | { status: 'error'; provider?: TOAuthProvider; error: string };

export function useOAuthPopup() {
  const openOAuthPopup = useCallback((provider: TOAuthProvider, token: string): Promise<OAuthPopupResult> => {
    return new Promise((resolve) => {
      if (!token) {
        resolve({ status: 'error', error: 'User not authenticated' });
        return;
      }
      const width = 500;
      const height = 600;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const host = env.apiHost;
      const port = env.apiPort ? `:${env.apiPort}` : '';

      const popup = window.open(
        `${host}${port}/connect/${provider}?token=${token}`,
        `${provider}-oauth`,
        `width=${width},height=${height},top=${top},left=${left}`
      );

      if (!popup) {
        resolve({ status: 'error', error: 'Popup blocked' });
        return;
      }

      let resolved = false;

      const handleMessage = (event: MessageEvent) => {
        // Filter out React DevTools and other non-OAuth messages
        if (
          event.data?.source === 'react-devtools-bridge' ||
          event.data?.source === 'react-devtools-content-script' ||
          event.data?.source === 'react-devtools-detector'
        ) {
          return;
        }

        // Only the popup's final page, which is served by this same web app
        if (event.origin !== window.location.origin) {
          return;
        }

        if (resolved) return;

        const { status, provider, error } = event.data || {};

        resolved = true;
        clearTimeout(timeoutId);
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);

        resolve({ status, provider, error });
      }; // Set up timeout to detect hung popups
      const timeoutId = setTimeout(
        () => {
          if (resolved) return;

          resolved = true;
          window.removeEventListener('message', handleMessage);

          if (popup && !popup.closed) {
            popup.close();
          }

          resolve({ status: 'error', error: 'OAuth timeout - popup did not respond' });
        },
        5 * 60 * 1000
      ); // 5 minute timeout

      // Check if popup was closed by user
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          if (resolved) {
            clearInterval(checkClosed);
            return;
          }

          resolved = true;
          clearInterval(checkClosed);
          clearTimeout(timeoutId);
          window.removeEventListener('message', handleMessage);

          resolve({ status: 'error', error: 'OAuth cancelled by user' });
        }
      }, 500);

      window.addEventListener('message', handleMessage);
    });
  }, []);

  return { openOAuthPopup };
}
